import React, { useState, useEffect, useCallback } from 'react';
import { Card, Spin } from 'antd';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { targetsAPI } from '../services/api';
import { toLocalTimeWithTZ } from '../utils/timezone';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

const LatencyChart = ({ targetId, filters = {}, logs: providedLogs = null, loading: providedLoading = null }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (providedLogs) return;
    if (!targetId) return;

    setLoading(true);
    try {
      // Determine limit based on time range
      let limit = 100; // Default for 5m
      switch (filters.timeRange) {
        case '5m':
          limit = 100;
          break;
        case '15m':
          limit = 300;
          break;
        case '30m':
          limit = 600;
          break;
        case '45m':
          limit = 900;
          break;
        case '1h':
          limit = 120;
          break;
        case '6h':
          limit = 720;
          break;
        case '24h':
          limit = 1440;
          break;
        case '7d':
          limit = 10080;
          break;
        case '30d':
          limit = 43200;
          break;
        default:
          limit = 100;
          break;
      }

      const params = { limit };

      // Add timeRange filter if provided
      if (filters.timeRange) {
        params.timeRange = filters.timeRange;
      }

      // Add custom date range if provided
      if (filters.startDate && filters.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }

      // Add status filter if provided
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      // Add other filters
      if (filters.latencyMin) {
        params.latencyMin = filters.latencyMin;
      }
      if (filters.latencyMax) {
        params.latencyMax = filters.latencyMax;
      }
      if (filters.hostFilter) {
        params.hostFilter = filters.hostFilter;
      }

      const response = await targetsAPI.getTargetLogs(targetId, params);
      if (response.success) {
        const sortedLogs = response.data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        setLogs(sortedLogs);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  }, [targetId, filters, providedLogs]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const logsToUse = providedLogs || logs;
  const isLoading = providedLoading ?? loading;
  const chartLogs = [...logsToUse].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const chartData = {
    datasets: [
      {
        label: 'Latency (ms)',
        data: chartLogs
          .filter(log => log.status === 'UP' && log.latency)
          .map(log => ({
            x: log.timestamp,
            y: log.latency,
            status: log.status,
            message: log.message || null
          })),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'DOWN',
        data: chartLogs
          .filter(log => log.status === 'DOWN')
          .map((log) => ({
            x: log.timestamp,
            y: 0, // Show at bottom
            status: log.status,
            message: log.message || log.error_message || null
          })),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.8)',
        showLine: false,
        pointStyle: 'crossRot',
        pointRadius: 8,
        pointHoverRadius: 10,
      },
      {
        label: 'TIMEOUT',
        data: chartLogs
          .filter(log => log.status === 'TIMEOUT')
          .map((log) => ({
            x: log.timestamp,
            y: 0, // Show at bottom
            status: log.status,
            message: log.message || log.error_message || null
          })),
        borderColor: 'rgb(255, 159, 64)',
        backgroundColor: 'rgba(255, 159, 64, 0.8)',
        showLine: false,
        pointStyle: 'triangle',
        pointRadius: 8,
        pointHoverRadius: 10,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Latency Over Time'
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          labelOld: function(context) {
            if (context.dataset.label === 'Latency (ms)') {
              return `✓ UP - Latency: ${context.parsed.y}ms`;
            } else if (context.dataset.label === 'DOWN') {
              return `✗ DOWN - Connection Failed`;
            } else if (context.dataset.label === 'TIMEOUT') {
              return `⏱ TIMEOUT - No Response`;
            }
          },
          label: function(context) {
            if (context.raw?.message) return context.raw.message;
            if (context.dataset.label === 'Latency (ms)') return `UP - Latency: ${context.parsed.y}ms`;
            if (context.dataset.label === 'DOWN') return 'DOWN';
            if (context.dataset.label === 'TIMEOUT') return 'TIMEOUT';
            return context.dataset.label;
          },
          afterLabel: function(context) {
            const timestamp = toLocalTimeWithTZ(context.raw.x);
            const utcTimestamp = context.raw.x;
            return [`Local: ${timestamp}`, `UTC: ${utcTimestamp}`];
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          displayFormats: {
            hour: 'HH:mm',
            day: 'MMM dd',
            month: 'MMM dd'
          }
        },
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Latency (ms)'
        },
        beginAtZero: true
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    elements: {
      point: {
        radius: 2,
        hoverRadius: 5
      }
    },
    parsing: {
      yAxisKey: 'y',
      xAxisKey: 'x'
    }
  };

  if (isLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', padding: '20px' }} />;
  }

  return (
    <Card title="Stats Chart">
      <div style={{ height: 300 }}>
        {logsToUse.length > 0 ? (
          <Line data={chartData} options={options} />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#999' }}>
            No data available
          </div>
        )}
      </div>
    </Card>
  );
};

export default LatencyChart;
